import { useCallback, useMemo } from "react";
import { HomeShow } from "@core/constants";
import { TableColumn } from "@components/Table/TableColumns";
import { HomeTableRawNormDataItem } from "@api/useHomeTableRawNormData";
import { useMacStageTableCell } from "@vms/TableCells/useMacStageTableCell";
import { useBaseSizeTableCell } from "@vms/TableCells/useBaseSizeTableCell";
import { useKeywordNameTableCell } from "@vms/TableCells/useKeywordNameTableCell";
import { useGetMetricRawTableCell } from "@vms/TableCells/useGetMetricRawTableCell";
import { HOME_TABLE_METRICS_POLARITY_ORDER, HOME_TABLE_METRICS_ORDER, Metric } from "@models/Metric";
import { getRem } from "@datassential/platform-ui-lib";
import { useGetMetricNormTableCell } from "@vms/TableCells/useGetMetricNormTableCell";

export type TableViewRawNormTableConfigProps = {
    data: HomeTableRawNormDataItem[];
    show: HomeShow;
    search: string;
}

export function useTableViewRawNormTableConfig({
    data,
    search,
    show,
}: TableViewRawNormTableConfigProps) {
    const trimmedSearch = useMemo(() => search.trim(), [search]);
    const hideSorting = useMemo(() => trimmedSearch.length > 0, [trimmedSearch]);

    const nameColumn = useKeywordNameTableCell(data, trimmedSearch);
    const macStageColumn = useMacStageTableCell(data);
    const baseSizeColumn = useBaseSizeTableCell(data);
    const getMetricRaw = useGetMetricRawTableCell(data, HOME_TABLE_METRICS_POLARITY_ORDER);
    const getMetricNorm = useGetMetricNormTableCell(data, HOME_TABLE_METRICS_POLARITY_ORDER);

    const getMetricStyle = useCallback(
        (index: number) => {
            return index === 3 ? {
                borderLeftWidth: getRem(3),
                paddingLeft: getRem(20),
            } : undefined;
        },
        []
    );

    const polarityIndex = useMemo(() => HOME_TABLE_METRICS_POLARITY_ORDER.findIndex((metric) => metric === Metric.PolarityScore), []);

    const rawMetrics = useMemo(
        () => HOME_TABLE_METRICS_ORDER.map(
            (metric, index) => getMetricRaw(index)
        ),
        [getMetricRaw]
    );

    const normMetrics = useMemo(
        () => HOME_TABLE_METRICS_ORDER.map(
            (_, index) => getMetricNorm(index)
        ),
        [getMetricNorm]
    );

    const polarityMetricColumn = useMemo(
        () => getMetricRaw(polarityIndex),
        [getMetricRaw, polarityIndex]
    );

    return useMemo(
        (): TableColumn[] => [
            {
                ...nameColumn,
                sortKey: hideSorting ? undefined : 'name',
            },
            {
                ...macStageColumn,
                sortKey: hideSorting ? undefined : 'macStage',
            },
            {
                ...baseSizeColumn,
                sortKey: hideSorting ? undefined : 'base',
            },
            ...rawMetrics.map((rawConfig, index) => ({
                ...rawConfig,
                sortKey: hideSorting ? undefined : `metric${index}`,
                isHidden: show === HomeShow.Norm,
                isHiddenForTextMode: true,
                style: {
                    ...rawConfig.style,
                    cell: {
                        ...rawConfig.style?.cell,
                        ...getMetricStyle(index),
                    },
                },
            })),
            ...normMetrics.map((normConfig, index) => ({
                ...normConfig,
                sortKey: hideSorting ? undefined : `metric${index}`,
                isHidden: show === HomeShow.Raw,
                isHiddenForTextMode: true,
                style: {
                    ...normConfig.style,
                    cell: {
                        ...normConfig.style?.cell,
                        ...getMetricStyle(index),
                    },
                },
            })),
            {
                ...polarityMetricColumn,
                sortKey: hideSorting ? undefined : `metric${polarityIndex}`,
                isHiddenForTextMode: true,
                style: {
                    ...polarityMetricColumn.style,
                    cell: {
                        ...polarityMetricColumn.style?.cell,
                        ...getMetricStyle(polarityIndex),
                    },
                },
            },
            {
                key: 'raw-metrics',
                isHiddenForRender: true,
                head: '',
                headText: 'Raw',
                Value: () => null,
                subColumns: rawMetrics,
            },
            {
                key: 'norm-metrics',
                isHiddenForRender: true,
                head: '',
                headText: 'Norms',
                Value: () => null,
                subColumns: normMetrics,
            },
            {
                key: 'polarity-metrics',
                isHiddenForRender: true,
                head: '',
                headText: 'Polarity',
                Value: () => null,
                subColumns: [{ ...polarityMetricColumn, headText: "" }],
            },
        ],
        [
            nameColumn,
            macStageColumn,
            baseSizeColumn,
            rawMetrics,
            normMetrics,
            show,
            hideSorting,
            getMetricStyle,
            polarityIndex,
            polarityMetricColumn,
        ]
    );
}